import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { Typography, Link } from '@material-ui/core'
import * as Modal from '_components/modal/generic-modal'
import { Button } from '@refera/ui-web'
import { useDispatch } from 'react-redux'
import { useParams } from '@reach/router'
import { useToast } from '_hooks/use-toast'
import { formatErrorMessage } from '_views/finance/utils/FormatErrorMessage'
import { transitionStepStatus, getServiceOrder } from '_modules/service-orders/actions'
import { getHistoryLogs } from '_modules/history-logs/actions'
import PersonIcon from '@mui/icons-material/Person'
import GroupsIcon from '@mui/icons-material/Groups'
import classnames from 'classnames'
import useStyles from './styles'

const PENDING_STAGES = {
  OWNER: 'pending_owner',
  THIRD_PARTY: 'pending_third_party',
}

const MoveToPendingStageModal = ({ isOpen, handleClose, serviceOrderId: propServiceOrderId }) => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { serviceOrderId: paramsServiceOrderId } = useParams()
  const serviceOrderId = propServiceOrderId || paramsServiceOrderId
  const { showToast } = useToast()
  const [selected, setSelected] = useState(null) // 'owner' | 'third_party'
  const [isLoading, setIsLoading] = useState(false)

  const handleSelect = useCallback(value => {
    setSelected(value)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!selected || !serviceOrderId) return
    const to = selected === 'owner' ? PENDING_STAGES.OWNER : PENDING_STAGES.THIRD_PARTY
    try {
      setIsLoading(true)
      await dispatch(transitionStepStatus(serviceOrderId, { to }))
      await dispatch(getServiceOrder(serviceOrderId))
      await dispatch(getHistoryLogs(serviceOrderId, true))
      showToast({ type: 'success', message: 'Ação realizada com sucesso.' })
      handleClose()
    } catch (error) {
      showToast({ type: 'error', message: formatErrorMessage(error) })
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, handleClose, selected, serviceOrderId, showToast])
  return (
    <Modal.Root open={isOpen} disableEscapeKeyDown className={styles.root}>
      <Modal.TitleModal title="Confirmação de Pendência">
        <hr className={styles.headerLine} />
      </Modal.TitleModal>
      <Modal.Content>
        <Modal.InfoIcon />
        <div className={styles.headerInfo}>
          <Typography className={styles.question}>
            O chamado deverá ficar pendente para quem?
          </Typography>
          <Typography className={styles.subQuestion}>
            Selecione o responsável da solução do problema do chamado.
          </Typography>
          <div className={styles.additionalInfo}>
            <Link
              href="/ajuda/pendencia"
              className={styles.additionalLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Clique aqui
            </Link>
            <Typography className={styles.additionalText}>para saber mais.</Typography>
          </div>
        </div>

        <div className={styles.optionGroup}>
          <div
            className={classnames(styles.optionWrapper, {
              [styles.optionSelected]: selected === 'owner',
            })}
            onClick={() => handleSelect('owner')}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && handleSelect('owner')}
          >
            <div className={styles.optionCard}>
              <div className={styles.optionIcon}>
                <PersonIcon fontSize="large" />
              </div>
              <Typography className={styles.optionTitle}>Proprietário</Typography>
            </div>
          </div>

          <div
            className={classnames(styles.optionWrapper, {
              [styles.optionSelected]: selected === 'third_party',
            })}
            onClick={() => handleSelect('third_party')}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && handleSelect('third_party')}
          >
            <div className={styles.optionCard}>
              <div className={styles.optionIcon}>
                <GroupsIcon fontSize="large" />
              </div>
              <Typography className={styles.optionTitle}>Terceiros</Typography>
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <div className={styles.footerActions}>
          <div className={styles.footerRight}>
            <Button color="red" variant="secondary" onClick={handleClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button
              className={styles.confirmButton}
              color="primary"
              variant="primary"
              onClick={handleConfirm}
              disabled={!selected || isLoading}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </Modal.Actions>
    </Modal.Root>
  )
}

export default MoveToPendingStageModal

MoveToPendingStageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  serviceOrderId: PropTypes.string,
}

MoveToPendingStageModal.defaultProps = {
  serviceOrderId: null,
}
